import React, { lazy, Suspense, useContext, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Outlet, Navigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Login from "./pages/Login";
import toastConfig from "./lib/config/toast";
import { AppProvider, AppState, silentLogin } from "./lib/context/AppProvider";
import { AlarmHistoryProvider } from "./lib/context/AlarmHistoryProvider";
import UnclassHeader from "./components/UnclassHeader";
import './styles/App.css';
import { getDomainUrl, getSpaceCockpitUrl, LOGIN_EXPIRATION, setLoggedInUser } from "./utils/general";
import { setSCReady } from "./lib/scReady";
import { EulaPage } from './components/EulaModal';
import ToastListener from "./components/ToastListener";
import PropTypes from 'prop-types';
import ExerciseHeader from "./components/ExerciseHeader";
import MotdHeaders from "./components/MotdHeader";
import NetworkDiagnostics from "./components/diagnostics/NetworkDiagnostics";

// Every page except Login is lazy-loaded so the login route (the first thing
// a user downloads, possibly on a very slow connection) only ships the entry
// chunk. Each page's JS/CSS is fetched on first navigation to it.
const NotFound = lazy(() => import("./pages/NotFound"));
const Spacefana = lazy(() => import("./pages/Spacefana"));
const DashboardProvider = lazy(() => import("./lib/context/DashboardProvider"));
const Chat = lazy(() => import("./pages/Chat"));
const Pair = lazy(() => import("./pages/Pair"));
const Event = lazy(() => import("./pages/Event"));
const Settings = lazy(() => import("./pages/Settings"));
const Home = lazy(() => import("./pages/Home"));
const Sam = lazy(() => import("./pages/Sam"));
const SamApp = lazy(() => import('./pages/SamApp'));
const Alert = lazy(() => import('./pages/Alert'));
const SpaceCockpit = lazy(() => import("./pages/SpaceCockpit"));
const Api = lazy(() => import("./pages/Api"));
const Credits = lazy(() => import('./pages/Credits'));
const JoinGroup = lazy(() => import('./pages/JoinGroup'));
const EmailWhitelist = lazy(() => import("./pages/Whitelist"));
const AccessLog = lazy(() => import("./pages/AccessLog"));
const ManageMOTD = lazy(() => import("./pages/ManageMOTD"));
const LaunchDetections = lazy(() => import("./pages/LaunchDetections"));
const Reports = lazy(() => import("./pages/Reports"));
const FireControlPage = lazy(() => import("./pages/FireControl"));
const AccountSetup = lazy(() => import("./pages/AccountSetup"));

const checkLoggedIn = (user, pendingRedirect, location) => {
    if (!user?.loggedIn || (!user.isGuest && !user.access_token)) {
        pendingRedirect.current = location?.pathname + location?.hash + location?.search;
        return <Navigate to={"/"} replace />;
    }
    return null;
}

const isLoginExpired = (user, setUser, setAuthChecking) => {
    if ((Date.now() - user.lastLoggedIn) >= LOGIN_EXPIRATION) {
        setAuthChecking(true);

        silentLogin()
            .then((data) => {
                setLoggedInUser(data, setUser); // good to go
            })
            .catch((err) => {
                console.error('Silent re-auth failed:', err);
                // failed login, return to home
                globalThis.location = '/';
            })
            .finally(() => setAuthChecking(false));

        return true;
    }
    
    return false;
}

const SessionReauthPlaceholder = () => {
    return <div style={{
        position: 'absolute',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.4)',
        pointerEvents: 'all',
        flexDirection: "column",
        color: "white"
    }}>
        <span className="mb-2">Refreshing login...</span>
        <Spinner animation="border" variant="light" />
    </div>;
};

const SessionCheckingPlaceholder = () => (
    <div style={{
        flexGrow: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    }}>
        <Spinner animation="border" variant="light" />
    </div>
);

const outletWrapperStyle = {
    flexGrow: 1,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
};

const routeCheck = (
    user, setUser, pendingRedirect, authChecking, setAuthChecking,
    location,
    checkSuperuser
) => {
    // if we are logging in, and the user was NOT logged in, return a opaque loading placeholder
    if (authChecking && !user.loggedIn) return <SessionCheckingPlaceholder />;

    // we are not logging in, but user is still not logged in - redirect to home page
    const resp = checkLoggedIn(user, pendingRedirect, location);
    if(resp) return resp;

    // we are logging in or the user was already logged in but expired, return a transparent one that keeps the state of the page while we login in background
    const reAuthing = authChecking || isLoginExpired(user, setUser, setAuthChecking);

    if (checkSuperuser && !reAuthing && !user.isSuperuser) { // not a superuser - redirect to home page
        pendingRedirect.current = null;
        return <Navigate to={"/home"} replace />;
    }

    return (
        <div style={outletWrapperStyle}>
            {reAuthing && <SessionReauthPlaceholder />}
            <Outlet />
        </div>
    );
};

export const ProtectedRoute = () => {
    const {user, setUser, pendingRedirect, authChecking} = useContext(AppState);
    const location = useLocation();

    const [reauthing, setReauthing] = useState(false);

    return routeCheck(user, setUser, pendingRedirect, authChecking || reauthing, setReauthing,
        location, false);
};

export const SuperuserRoute = () => {
    const {user, setUser, pendingRedirect, authChecking} = useContext(AppState);
    const location = useLocation();

    const [reauthing, setReauthing] = useState(false);

    return routeCheck(user, setUser, pendingRedirect, authChecking || reauthing, setReauthing,
        location, true);
};

export const AppRoutes = ({dashboardElement}) => (
    <Suspense fallback={<SessionCheckingPlaceholder />}>
    <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/eula" element={<EulaPage />} />
        <Route path="/signup" element={<AccountSetup isReset={false} />} />
        <Route path="/reset" element={<AccountSetup isReset={true} />} />
        <Route element={<ProtectedRoute />}>
            {
                ["/dashboards/*"].map((p) =>
                    (<Route key={p} path={p} element={dashboardElement} />)
                )
            }
            <Route path="/sam" element={<Sam />} />
            <Route path="/samapp/:appId" element={<SamApp />} />
            <Route path="/spacecockpit" element={<SpaceCockpit />} />
            <Route path="/pair" element={<Pair />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/api" element={<Api />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/home" element={<Home />} />
            <Route path="/event/:notsoid" element={<Event />} />
            <Route path="/event/:type/*" element={<Event />} />
            <Route path="/event/:id" element={<Event />} />
            <Route path="/alert/:id" element={<Alert />} />
            <Route path="/credits" element={<Credits />} />
            <Route path="/join/:id" element={<JoinGroup />} />
            <Route path="/launch-activity" element={<LaunchDetections />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="*" element={<NotFound />} />
        </Route>
        <Route element={<SuperuserRoute />}>
            <Route path="/whitelist" element={<EmailWhitelist />} />
            <Route path="/accesses" element={<AccessLog />} />
            <Route path="/banners" element={<ManageMOTD />} />
        </Route>
        <Route element={<SuperuserRoute />}>
            <Route path="/battle-loop" element={<FireControlPage />} />
            <Route path="/singularity" element={<FireControlPage />} />
        </Route>
    </Routes>
    </Suspense>
);

AppRoutes.propTypes = {
  dashboardElement: PropTypes.node
};

export const SpaceCockpitAnchor = () => {
    const { user, scQueryParams, userDetails } = useContext(AppState);
    const accessToken = user?.access_token;
    const includeExerciseData = userDetails?.Settings?.includeExerciseData === "Yes";

    // SC posts {ready: true} once per load; record it globally so scene posts
    // are held back until the frame can actually receive them. Listening here
    // (not in SpaceCockpitFrame) so the signal isn't missed when SC finishes
    // preloading on a page without a Space Cockpit panel.
    useEffect(() => {
        const handleMessage = (e) => {
            if (e.origin !== getDomainUrl("spacecockpit")) return;
            let msg = e.data;
            if (typeof msg === "string") {
                try { msg = JSON.parse(msg); } catch { return; }
            }
            if (msg?.ready) setSCReady(true);
        };
        globalThis.addEventListener("message", handleMessage);
        return () => globalThis.removeEventListener("message", handleMessage);
    }, []);

    const preload = userDetails?.Settings?.preloadSC !== "No";

    const iframeContainerStyle = {
        width: "50px", 
        height: "50px",
        position: "absolute",
        zIndex: 1000,
        visibility: "hidden"
    };

    const iframeStyle = {
        width: '100%',
        height: '100%',
        border: 'none',
        borderRadius: '8px',
        backgroundColor: '#1a1d20'
    };

    const getUrl = () => {
        if (!preload || !accessToken?.length) return ""; // only load when we have token
        return getSpaceCockpitUrl(accessToken, scQueryParams, includeExerciseData);
    };

    return (
        <div style={iframeContainerStyle} id = "sc-frame-anchor">
            <iframe
                id="sc-frame"
                src={getUrl()}
                style={iframeStyle}
                title="Space Cockpit"
                aria-label="Space Cockpit Dashboard"
                allow="geolocation; clipboard-read; clipboard-write; accelerometer; gyroscope;"
                sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-downloads"
                >
            </iframe>
        </div>
    );
}

export const AppContents = () => (
    <>
        <ToastContainer {...toastConfig} />
        <ToastListener />
        <SpaceCockpitAnchor />
        <div style={{
            display: "flex",
            flexDirection: "column",
            height: "100vh"
        }}>
            <UnclassHeader />
            <ExerciseHeader />
            <MotdHeaders />

            <AppRoutes dashboardElement={
                <DashboardProvider>
                    <Spacefana />
                </DashboardProvider>
            } />
        </div>
        {/* Overlays every page; renders nothing unless the user enabled it in Settings. */}
        <NetworkDiagnostics />
    </>
);

export const AppRouter = ({children}) => (
    <Router basename={"/"} future={{v7_startTransition: true,v7_relativeSplatPath: true}}>
        {children}
    </Router>
);

AppRouter.propTypes = {
  children: PropTypes.node
};

export const App = () => {
    return (
        <AppRouter>
            <AppProvider>
                <AlarmHistoryProvider>
                    <AppContents />
                </AlarmHistoryProvider>
            </AppProvider>
        </AppRouter>
    );
};